/**
 * TYPING INTO A CARD LOOKUP SEARCHES THE SERVER (KPR-466).
 *
 * The picker's options used to be the first page of the target card (the
 * API's default 50, newest first), filtered in the browser as the person
 * typed. A card past 50 records — the published Medications collection's
 * 1,102 products, an OAR chapter's hundreds of rules — then hid most of its
 * records from the typeahead: "lisinopril" found nothing because no
 * lisinopril product happened to be among the newest 50, which reads exactly
 * like "there is no lisinopril".
 *
 * So a typed term goes to `GET /api/items?q=`, in the same scope the first
 * page is fetched in (collection, card and its descendants, the picker
 * filter) and under the caller's own read scope — the server decides what
 * may be seen; nothing here widens it. No API change: KQL's `~` over the
 * target's NAME elements is the API's existing name search.
 *
 * WHICH ELEMENTS ARE "THE NAME". The API's own rule for a card's title
 * element (keepr-api utils/kql/compile.js buildVocabulary — what a lookup
 * compared by text resolves through): `options.isTitle`, else the first
 * text-small/text-large element, else the first untyped one. Plus the
 * text elements the card's display template names, because the option LABEL
 * is the template and a person types what they read ("F-150" when the label
 * is "{{make}} {{model}}"). Rejected: searching every text element — a
 * product's long brand-name list or notes would match "a" on every record
 * and push the record the person means off the first page. Rejected too: a
 * server-side match on the stored `displayValue`, which needs a new KQL
 * system field (an API and vendored-parser change, and `title` is a common
 * element name) for what the title + template elements already answer.
 *
 * EACH WORD must match one of those elements, so "lisinopril 10" finds
 * "lisinopril 10 MG Oral Tablet" and "ford f-150" finds a make + model
 * template. A phrase-only match would miss the second.
 */
import { parse, serialize, cmp, or, and, str, conditionCount, elementIdSegment, SYSTEM_FIELDS } from './kql';
import { andFilter } from './lookupFilter';
import { parseStoredTemplate } from './displayTemplate';
import { normalizeId } from './ids';

/** The element types `~` reads as text — what a typed word can match. */
const TEXT_TYPES = new Set(['text-small', 'text-large', 'email', 'phone']);
const isTextLike = def => !!def && (TEXT_TYPES.has(def.dataType) || !def.dataType);

const ELEMENT_ID_RE = /^[a-z0-9]{8}$/;

// KQL's own limits (docs/SCHEMA.md § KQL → Limits): 50 conditions and 2,000
// characters per query, the picker filter included. Over either the API
// answers 400 and the person would see an error for typing too much, so the
// query is built to fit — words that do not fit are left to the browser's
// filter over what came back.
export const KQL_MAX_CONDITIONS = 50;
export const KQL_MAX_LENGTH = 2000;
// A name is a few words. Past these the extra words only cost conditions.
export const MAX_SEARCH_WORDS = 6;
export const MAX_SEARCH_TERM_LENGTH = 100;
// Fields searched per word. Five cards under a target, each with its own
// title and a two-element template, is already more than any real lookup.
export const MAX_SEARCH_FIELDS = 8;

/** The API's title element over a card's merged elements. */
export function titleElementOf(elements) {
	const list = Array.isArray(elements) ? elements : [];
	return list.find(el => el?.options?.isTitle)
		|| list.find(el => el && (el.dataType === 'text-small' || el.dataType === 'text-large'))
		|| list.find(el => el && !el.dataType)
		|| null;
}

/** The display template a card's items render with: its own, else the nearest ancestor's. */
function templateFor(cardId, cardsById) {
	const seen = new Set();
	let id = normalizeId(cardId);
	while (id && !seen.has(id)) {
		seen.add(id);
		const card = cardsById?.[id];
		if (!card) return '';
		if (card.displayTemplate) return card.displayTemplate;
		id = normalizeId(card.parentCardId);
	}
	return '';
}

/**
 * The KQL path segment for an element: its immutable id (`#k7f3q2xa`) when
 * it has one — never ambiguous across the cards in scope, never renamed —
 * else its name. A name that is also a system field (`notes`, `created`…)
 * is quoted by the serializer through `forceElement`.
 */
const fieldOf = def => {
	if (typeof def?.id === 'string' && ELEMENT_ID_RE.test(def.id)) return { key: `#${def.id}`, path: [elementIdSegment(def.id)], forceElement: false };
	if (typeof def?.name === 'string' && def.name) return { key: def.name, path: [def.name], forceElement: SYSTEM_FIELDS.includes(def.name) };
	return null;
};

/**
 * lookupSearchFields({ cardIds, cardsById, getElementsForCard })
 *   -> [{ key, path, forceElement }]
 *
 * The name elements of every card the picker lists (the target and the
 * cards beneath it), de-duplicated — an inherited title is one field. Empty
 * when none of the cards is known yet: the caller then filters the first
 * page in the browser, as before, rather than send a query naming nothing.
 */
export function lookupSearchFields({ cardIds, cardsById, getElementsForCard }) {
	const out = [];
	const seen = new Set();
	const add = def => {
		const field = fieldOf(def);
		if (!field || seen.has(field.key) || out.length >= MAX_SEARCH_FIELDS) return;
		seen.add(field.key);
		out.push(field);
	};
	[...(cardIds || [])].forEach(rawId => {
		const id = normalizeId(rawId);
		if (!id || !cardsById?.[id]) return;
		const elements = (typeof getElementsForCard === 'function' ? getElementsForCard(id) : null) || cardsById[id].elements || [];
		const title = titleElementOf(elements);
		if (title) add(title);
		parseStoredTemplate(templateFor(id, cardsById)).forEach(segment => {
			if (segment?.type !== 'element') return;
			const def = elements.find(el => el?.name === segment.name);
			if (isTextLike(def)) add(def);
		});
	});
	return out;
}

/** The words of a typed term: trimmed, capped, de-duplicated (case-folded). */
export function searchWords(term) {
	const text = String(term == null ? '' : term).trim().slice(0, MAX_SEARCH_TERM_LENGTH);
	if (!text) return [];
	const seen = new Set();
	return text.split(/\s+/).filter(word => {
		const key = word.toLowerCase();
		if (!word || seen.has(key)) return false;
		seen.add(key);
		return true;
	}).slice(0, MAX_SEARCH_WORDS);
}

const fits = text => {
	if (text.length > KQL_MAX_LENGTH) return false;
	try {
		return conditionCount(parse(text)) <= KQL_MAX_CONDITIONS;
	} catch (e) {
		return false;
	}
};

/**
 * lookupSearchQuery({ fields, term, filter }) -> string
 *
 * The `?q=` for a typed term: every word matches one of the fields, ANDed
 * with the picker filter when there is one. '' when nothing can be asked —
 * no term, no field, or not even one word fits beside the filter.
 */
export function lookupSearchQuery({ fields, term, filter = '' }) {
	const list = Array.isArray(fields) ? fields.filter(f => f && Array.isArray(f.path)) : [];
	let words = searchWords(term);
	if (!list.length || !words.length) return '';
	while (words.length) {
		const clauses = words.map(word => {
			const each = list.map(f => cmp(f.path, 'contains', str(word), { forceElement: !!f.forceElement }));
			return each.length === 1 ? each[0] : or(each);
		});
		const text = andFilter(filter, serialize(clauses.length === 1 ? clauses[0] : and(clauses)));
		if (fits(text)) return text;
		words = words.slice(0, -1);
	}
	return '';
}
